import React, { useState, useEffect } from 'react';
import { Smartphone, Plus, Trash2, Save, ToggleLeft, ToggleRight, AlertCircle, Check, X, Pencil, Users, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../services/api';
import { GlassButton } from '../../components/ui/GlassButton';

interface ConnectedAccount {
    id: string;
    phone: string;
    user_id: string;
    connected_at: string;
}

interface TelegramApp {
    id: string;
    label: string;
    api_id: string;
    api_hash: string;
    max_accounts: number;
    is_active: boolean;
    priority: number;
    accounts_count: number;
    connected_accounts: ConnectedAccount[];
    created_at: string;
}

export const TelegramAppsList: React.FC = () => {
    const [apps, setApps] = useState<TelegramApp[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedApp, setExpandedApp] = useState<string | null>(null);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const [formData, setFormData] = useState({
        label: '',
        api_id: '',
        api_hash: '',
        max_accounts: 10
    });

    useEffect(() => {
        loadApps();
    }, []);

    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(t);
        }
    }, [toast]);

    const loadApps = async () => {
        try {
            setLoading(true);
            const res = await api.get('/banana/telegram-apps');
            setApps(res.data.apps || []);
        } catch {
            setToast({ msg: 'Erro ao carregar aplicativos.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({ label: '', api_id: '', api_hash: '', max_accounts: 10 });
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = async () => {
        if (!formData.api_id || !formData.api_hash) {
            setToast({ msg: 'API ID e API Hash são obrigatórios.', type: 'error' });
            return;
        }

        try {
            if (editingId) {
                await api.patch(`/banana/telegram-apps/${editingId}`, formData);
                setToast({ msg: 'Aplicativo atualizado com sucesso.', type: 'success' });
            } else {
                await api.post('/banana/telegram-apps', formData);
                setToast({ msg: 'Aplicativo criado com sucesso.', type: 'success' });
            }
            resetForm();
            loadApps();
        } catch (err: any) {
            setToast({ msg: err.response?.data?.error || 'Erro ao salvar aplicativo.', type: 'error' });
        }
    };

    const handleEdit = (app: TelegramApp) => {
        setFormData({
            label: app.label,
            api_id: app.api_id,
            api_hash: app.api_hash,
            max_accounts: app.max_accounts
        });
        setEditingId(app.id);
        setShowForm(true);
    };

    const handleToggle = async (app: TelegramApp) => {
        try {
            await api.patch(`/banana/telegram-apps/${app.id}`, { is_active: !app.is_active });
            setToast({ msg: `Aplicativo ${!app.is_active ? 'ativado' : 'desativado'}.`, type: 'success' });
            loadApps();
        } catch {
            setToast({ msg: 'Erro ao alterar status.', type: 'error' });
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Tem certeza? Contas vinculadas ser\u00e3o desvinculadas deste aplicativo.')) return;
        try {
            await api.delete(`/banana/telegram-apps/${id}`);
            setToast({ msg: 'Aplicativo removido.', type: 'success' });
            if (expandedApp === id) setExpandedApp(null);
            loadApps();
        } catch {
            setToast({ msg: 'Erro ao remover.', type: 'error' });
        }
    };

    const totalAccounts = apps.reduce((sum, a) => sum + a.accounts_count, 0);
    const totalCapacity = apps.filter(a => a.is_active).reduce((sum, a) => sum + a.max_accounts, 0);

    const maskPhone = (phone: string) => {
        if (!phone) return '---';
        const d = phone.replace(/\D/g, '');
        if (d.length >= 12) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ****-${d.slice(-4)}`;
        if (d.length >= 10) return `(${d.slice(0, 2)}) ****-${d.slice(-4)}`;
        return phone;
    };

    return (
        <div className="p-6 text-white">
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 text-sm ${toast.type === 'success' ? 'bg-green-600/90 text-white' : 'bg-red-600/90 text-white'}`}>
                    {toast.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                    {toast.msg}
                </div>
            )}

            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Smartphone className="text-orange-500" /> Aplicativos Telegram
                    </h1>
                    <p className="text-sm text-gray-400 mt-1">
                        Gerencie os aplicativos usados para conectar contas. O sistema preenche cada app at&eacute; o limite antes de usar o pr&oacute;ximo.
                    </p>
                </div>
                <GlassButton onClick={() => { resetForm(); setShowForm(true); }}>
                    <Plus size={16} className="mr-1" /> Novo Aplicativo
                </GlassButton>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Apps Ativos</div>
                    <div className="text-2xl font-bold text-orange-400 mt-1">{apps.filter(a => a.is_active).length}</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Contas Conectadas</div>
                    <div className="text-2xl font-bold text-blue-400 mt-1">{totalAccounts}</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Capacidade Total</div>
                    <div className="text-2xl font-bold text-green-400 mt-1">{totalAccounts} / {totalCapacity}</div>
                </div>
            </div>

            {showForm && (
                <div className="bg-white/5 border border-orange-500/20 rounded-xl p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        {editingId ? <Pencil size={18} /> : <Plus size={18} />}
                        {editingId ? 'Editar Aplicativo' : 'Novo Aplicativo'}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">Nome / Label</label>
                            <input
                                type="text"
                                value={formData.label}
                                onChange={e => setFormData({ ...formData, label: e.target.value })}
                                placeholder="Ex: App Principal"
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">API ID *</label>
                            <input
                                type="text"
                                value={formData.api_id}
                                onChange={e => setFormData({ ...formData, api_id: e.target.value })}
                                placeholder="Ex: 38831151"
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">API Hash *</label>
                            <input
                                type="text"
                                value={formData.api_hash}
                                onChange={e => setFormData({ ...formData, api_hash: e.target.value })}
                                placeholder="Ex: 692924f2d4a4f985..."
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">Limite M&aacute;ximo de Contas</label>
                            <input
                                type="number"
                                value={formData.max_accounts}
                                onChange={e => setFormData({ ...formData, max_accounts: parseInt(e.target.value) || 10 })}
                                min={1}
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex gap-3 mt-4">
                        <GlassButton onClick={handleSubmit}>
                            <Save size={14} className="mr-1" /> {editingId ? 'Salvar' : 'Criar'}
                        </GlassButton>
                        <button
                            onClick={resetForm}
                            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                            <X size={14} className="inline mr-1" /> Cancelar
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-12 text-gray-500">Carregando...</div>
            ) : apps.length === 0 ? (
                <div className="text-center py-12">
                    <Smartphone size={48} className="mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400 mb-2">Nenhum aplicativo configurado.</p>
                    <p className="text-sm text-gray-500">Adicione aplicativos com API ID e Hash para conectar contas Telegram.</p>
                    <p className="text-xs text-gray-600 mt-2">Enquanto nenhum app estiver configurado, o sistema usar&aacute; as credenciais padr&atilde;o.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {apps.map((app, index) => {
                        const usage = app.max_accounts > 0 ? (app.accounts_count / app.max_accounts) * 100 : 0;
                        const isFull = app.accounts_count >= app.max_accounts;

                        return (
                            <div
                                key={app.id}
                                className={`bg-white/5 border rounded-xl transition-all ${app.is_active ? 'border-white/10 hover:border-orange-500/30' : 'border-red-500/20 opacity-60'}`}
                            >
                                <div className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${app.is_active ? 'bg-orange-500/20 text-orange-400' : 'bg-red-500/20 text-red-400'}`}>
                                                #{index + 1}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-white truncate">{app.label || 'Aplicativo Telegram'}</span>
                                                    {isFull && (
                                                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded-full uppercase font-bold">Lotado</span>
                                                    )}
                                                    {!app.is_active && (
                                                        <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-[10px] rounded-full uppercase font-bold">Inativo</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    API ID: {app.api_id}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 ml-4">
                                            <div className="text-right mr-4 hidden sm:block">
                                                <div className="text-sm font-mono text-white">{app.accounts_count}/{app.max_accounts}</div>
                                                <div className="w-24 h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all ${usage >= 90 ? 'bg-red-500' : usage >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                                        style={{ width: `${Math.min(usage, 100)}%` }}
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                                                className="p-2 rounded-lg hover:bg-white/10 transition-colors flex items-center"
                                                title="Ver contas conectadas"
                                            >
                                                <Users size={16} className="text-blue-400" />
                                                {expandedApp === app.id ? <ChevronUp size={12} className="text-gray-400 ml-0.5" /> : <ChevronDown size={12} className="text-gray-400 ml-0.5" />}
                                            </button>

                                            <button
                                                onClick={() => handleToggle(app)}
                                                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                                                title={app.is_active ? 'Desativar' : 'Ativar'}
                                            >
                                                {app.is_active
                                                    ? <ToggleRight size={20} className="text-green-400" />
                                                    : <ToggleLeft size={20} className="text-gray-500" />
                                                }
                                            </button>

                                            <button
                                                onClick={() => handleEdit(app)}
                                                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                                                title="Editar"
                                            >
                                                <Pencil size={16} className="text-gray-400" />
                                            </button>

                                            <button
                                                onClick={() => handleDelete(app.id)}
                                                className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
                                                title="Excluir"
                                            >
                                                <Trash2 size={16} className="text-red-400" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {expandedApp === app.id && (
                                    <div className="border-t border-white/5 px-4 pb-4 pt-3">
                                        <h4 className="text-xs text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                                            <Users size={12} /> Contas Conectadas ({app.accounts_count})
                                        </h4>
                                        {app.connected_accounts && app.connected_accounts.length > 0 ? (
                                            <div className="space-y-1.5">
                                                {app.connected_accounts.map((acc) => (
                                                    <div key={acc.id} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-2 h-2 bg-green-500 rounded-full" />
                                                            <span className="text-gray-300 font-mono">{maskPhone(acc.phone)}</span>
                                                        </div>
                                                        <span className="text-xs text-gray-500">
                                                            {acc.connected_at ? new Date(acc.connected_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '---'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-600">Nenhuma conta conectada neste aplicativo.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-2">
                    <AlertCircle size={14} className="text-orange-400" /> Como funciona
                </h3>
                <ul className="text-xs text-gray-500 space-y-1">
                    <li>&#x2022; Novas contas s&atilde;o atribu&iacute;das ao primeiro aplicativo com vagas dispon&iacute;veis</li>
                    <li>&#x2022; Quando um app atinge o limite, o sistema automaticamente usa o pr&oacute;ximo ativo</li>
                    <li>&#x2022; Se nenhum app estiver dispon&iacute;vel, o sistema usa as credenciais padr&atilde;o do servidor</li>
                    <li>&#x2022; Contas j&aacute; vinculadas a um app continuam usando ele mesmo ap&oacute;s estar lotado</li>
                </ul>
            </div>
        </div>
    );
};
